import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useShop } from '@/state';
import { track } from '@/lib/analytics';
import { openManagerChat } from '@/lib/telegram';
import { IconMessage, IconSearch } from '@/components/icons';
import { EmptyState, ErrorState, ProductTile, Rail, TileSkeleton } from '@/components/ui';
import { isNetworkError, isPaused } from '@/lib/online';

const MANAGER = (import.meta.env.VITE_MANAGER_USERNAME as string | undefined) ?? 'alkor_manager';
const SUGGESTIONS = ['эхолот', 'спиннинг', 'воблер', 'палатка', 'прицел'];
const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

export function SearchScreen() {
  const [params, setParams] = useSearchParams();
  const initial = params.get('q') ?? '';
  const [input, setInput] = useState(initial);
  const [query, setQuery] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);
  const { inCart, addToCart, subscribeStock, busy } = useShop();

  // Debounce 300 мс: на каждый символ запрос не уходит
  useEffect(() => {
    const t = window.setTimeout(() => {
      setQuery(input);
      const next = new URLSearchParams(params);
      if (input) next.set('q', input);
      else next.delete('q');
      setParams(next, { replace: true });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const active = query.trim().length >= MIN_CHARS;

  const results = useQuery({
    queryKey: ['search', query.trim()],
    queryFn: () => api.products({ q: query.trim(), limit: 30 }),
    enabled: active,
    staleTime: 30_000,
  });

  const popular = useQuery({
    queryKey: ['home'],
    queryFn: api.home,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (active && results.data) {
      track('search', { props: { q: query.trim(), results: results.data.meta?.total ?? 0 } });
    }
  }, [active, query, results.data]);

  const items = results.data?.items ?? [];

  const contactManager = () => {
    void api.createLead({
      message: `Ищу товар: «${query.trim()}». В каталоге не нашёл.`,
      source: 'miniapp_empty_search',
      contextQuery: query.trim(),
    }).catch(() => undefined);
    track('manager_contact', { props: { source: 'empty_search', q: query.trim() } });
    openManagerChat(MANAGER, `Здравствуйте! Ищу: ${query.trim()}`);
  };

  return (
    <>
      <div className="searchbar" style={{ marginTop: 14 }}>
        <IconSearch size={17} />
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Название, бренд или артикул"
          autoComplete="off"
          enterKeyHint="search"
          aria-label="Поиск по каталогу"
        />
        {input && (
          <button type="button" className="searchbar__clear" aria-label="Очистить" onClick={() => setInput('')}>✕</button>
        )}
      </div>

      {!active && (
        <>
          <div className="block" style={{ marginTop: 22 }}>
            <div className="block__head"><h3>Часто ищут</h3></div>
            <div className="filters" style={{ padding: 0, marginTop: 0 }}>
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="fchip" onClick={() => setInput(s)}>{s}</button>
              ))}
            </div>
          </div>
          <Rail title="Хиты продаж" items={popular.data?.popular ?? []} />
        </>
      )}

      {/* Без связи запрос стоит на паузе — ошибки не будет никогда */}
      {active && isPaused(results) && (
        <ErrorState offline onRetry={() => void results.refetch()} />
      )}

      {active && results.isPending && !results.isError && !isPaused(results) && (
        <div className="block"><TileSkeleton count={4} /></div>
      )}

      {active && results.isError && (
        <ErrorState
          message={(results.error as Error).message}
          offline={isNetworkError(results.error)}
          onRetry={() => void results.refetch()}
        />
      )}

      {active && items.length > 0 && (
        <div className="block" style={{ marginTop: 16 }}>
          <div className="block__head"><h3>Найдено: {results.data?.meta?.total ?? items.length}</h3></div>
          <div className="plist">
            {items.map((p) => (
              <ProductTile
                key={p.id}
                product={p}
                inCart={inCart(p.id)}
                busy={busy}
                onAdd={(x) => void addToCart(x)}
                onNotify={(x) => void subscribeStock(x)}
              />
            ))}
          </div>
        </div>
      )}

      {active && results.isSuccess && items.length === 0 && (
        <>
          <EmptyState
            icon={<IconSearch size={24} />}
            title="Ничего не нашли"
            text={`По запросу «${query.trim()}» совпадений нет. Напишите, что нужно — подберём у поставщиков и назовём цену.`}
            action={
              <button type="button" className="secbtn" onClick={contactManager}>
                <IconMessage size={17} />
                Написать менеджеру
              </button>
            }
          />
          <Rail title="Возможно, подойдёт" items={popular.data?.popular ?? []} />
        </>
      )}

      <div className="pad" />
    </>
  );
}
